/**
 * Le regole che trasformano un dato annidato («data.landing.hero.ctaText») in
 * qualcosa che una persona sa compilare, senza mostrare JSON né la parola
 * «null». Pure e senza React, così si provano con i test.
 *
 * Perché esiste: i 569 contenuti importati dal vecchio sito portano strutture
 * che il tipo non dichiara (`extra`, `card`, `landing`…). Un tipo non può
 * descrivere ogni loro chiave, quindi il controllo si sceglie guardando il
 * nome della chiave e il valore che c'è.
 */

export type Bag = Record<string, unknown>

export function isBag(value: unknown): value is Bag {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Vuoto per una persona: assente, «null», stringa vuota o solo spazi. */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

/** Il testo da mostrare in un campo: mai «null» o «undefined». */
export function textOf(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

// ----- Etichette ----------------------------------------------------------------------

/** «ctaHref» → «Cta href», «hero_title» → «Hero title»: per le chiavi che il dizionario non conosce. */
export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/[._/-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key
}

/** `translate` è `t`: se la chiave del dizionario non esiste torna la chiave stessa, e allora si ripiega su `humanizeKey`. */
export function labelForKey(translate: (key: string) => string, key: string): string {
  if (/^\d+$/.test(key)) return key
  const dictKey = `editor.keys.${key}`
  const found = translate(dictKey)
  return found && found !== dictKey ? found : humanizeKey(key)
}

// ----- Chiavi di servizio -------------------------------------------------------------

const TECHNICAL_KEY = /^(lineage|source\w*|origin\w*|bodyFormat|imported\w*|legacy\w*|migrat\w*|checksum|hash|createdAt|updatedAt|_.*)$/i

/** Dati di servizio dell'importazione: si leggono in «Dettagli tecnici», non si modificano. */
export function isTechnicalKey(key: string): boolean {
  return TECHNICAL_KEY.test(key)
}

export interface TechnicalEntry {
  /** Il percorso con i nomi veri delle chiavi, per ritrovarlo. */
  path: string[]
  value: unknown
}

/** Tutte le chiavi di servizio, a qualunque profondità, senza entrare in quelle già raccolte. */
export function collectTechnical(data: Bag, skip: ReadonlySet<string> = new Set()): TechnicalEntry[] {
  const found: TechnicalEntry[] = []
  const walk = (value: unknown, path: string[]) => {
    if (!isBag(value)) return
    for (const [key, child] of Object.entries(value)) {
      if (path.length === 0 && skip.has(key)) continue
      if (isTechnicalKey(key)) { if (!isBlank(child)) found.push({ path: [...path, key], value: child }); continue }
      if (isBag(child)) walk(child, [...path, key])
    }
  }
  walk(data, [])
  return found
}

// ----- Quale controllo ----------------------------------------------------------------

export type ControlKind = 'switch' | 'number' | 'date' | 'html' | 'image' | 'url' | 'longtext' | 'text'

const IMAGE_WORDS = new Set(['image', 'img', 'immagine', 'cover', 'copertina', 'photo', 'foto', 'logo', 'thumbnail', 'poster', 'avatar', 'background', 'sfondo'])
const NOT_AN_ADDRESS_WORDS = new Set(['alt', 'caption', 'didascalia', 'title', 'titolo', 'text', 'testo', 'description', 'descrizione', 'color', 'colore', 'position', 'posizione', 'size', 'width', 'height', 'format', 'type', 'tipo'])
const URL_WORDS = new Set(['url', 'href', 'link', 'collegamento', 'canonical', 'permalink'])
const DATE_WORDS = new Set(['date', 'data', 'published', 'scheduled', 'expires'])
const LONG_WORDS = new Set(['description', 'descrizione', 'summary', 'riassunto', 'excerpt', 'subtitle', 'sottotitolo', 'abstract', 'text', 'answer', 'risposta', 'quote', 'note', 'notes'])
const BODY_KEYS = new Set(['body', 'content', 'testo', 'corpo', 'contenuto'])
const IMAGE_FILE = /\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i
const HAS_TAG = /<\/?[a-z][a-z0-9-]*(\s[^>]*)?>/i

/** «ctaHref» → ['cta', 'href']: le parole di una chiave, per riconoscerla senza fidarsi di maiuscole e trattini. */
export function wordsOf(key: string): string[] {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
}

export function looksLikeHtml(value: string): boolean {
  return HAS_TAG.test(value)
}

export function looksLikeImageAddress(value: string): boolean {
  return /^\/?media\//.test(value) || IMAGE_FILE.test(value.trim())
}

/** Il momento che una data in testo o in millisecondi rappresenta, o null. */
export function parseMoment(value: unknown): Date | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 1e11) return new Date(value)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    const moment = new Date(value.trim())
    return Number.isNaN(moment.getTime()) ? null : moment
  }
  return null
}

/** Un testo senza tag è HTML da modificare «a vista» solo se chi l'ha importato dice che lo è. */
export function isHtmlBody(key: string, value: unknown, siblings: Bag, bodyFormat?: string): boolean {
  const text = typeof value === 'string' ? value : ''
  if (text && looksLikeHtml(text)) return true
  const words = wordsOf(key)
  const bodyLike = BODY_KEYS.has(key.toLowerCase()) || words.includes('html')
  if (!bodyLike) return false
  const declared = typeof siblings.bodyFormat === 'string' ? siblings.bodyFormat : bodyFormat
  return declared === 'html' || (declared === undefined && isBlank(value) && words.includes('html'))
}

/**
 * `siblings` è l'oggetto che contiene la chiave: serve a capire che
 * `{ type: 'image', url: '…' }` è un'immagine e che `body` accanto a
 * `bodyFormat: 'html'` è un testo formattato. `bodyFormat` è lo stesso dato
 * quando sta altrove nel documento (`extra.bodyFormat`).
 */
export function controlKindFor(key: string, value: unknown, siblings: Bag = {}, bodyFormat?: string): ControlKind {
  if (typeof value === 'boolean') return 'switch'
  const words = wordsOf(key)
  if (typeof value === 'number') return words.some((w) => DATE_WORDS.has(w)) && parseMoment(value) ? 'date' : 'number'
  const text = typeof value === 'string' ? value : ''
  const blank = isBlank(value)
  if ((blank || typeof value === 'string') && isHtmlBody(key, value, siblings, bodyFormat)) return 'html'
  if (words.some((w) => DATE_WORDS.has(w)) && (blank || parseMoment(value)) && !words.includes('update')) return 'date'
  if (!blank && parseMoment(value) && text.length <= 30 && !text.includes(' ')) return 'date'
  const imageWord = words.some((w) => IMAGE_WORDS.has(w))
  const notAddress = words.some((w) => NOT_AN_ADDRESS_WORDS.has(w))
  if (imageWord && !notAddress) return 'image'
  if (text && looksLikeImageAddress(text)) return 'image'
  if (words.length === 1 && ['url', 'src', 'path'].includes(words[0] ?? '') && siblings.type === 'image') return 'image'
  if (words.some((w) => URL_WORDS.has(w)) || /^https?:\/\//i.test(text)) return 'url'
  if (text.includes('\n') || text.length > 90 || words.some((w) => LONG_WORDS.has(w))) return 'longtext'
  return 'text'
}

// ----- Pulizia dei valori -------------------------------------------------------------

/**
 * Ripulisce `next` dai valori che una persona non ha scritto: «null» e
 * «undefined» spariscono, e una stringa vuota sparisce se prima la chiave non
 * c'era. Così svuotare un campo mai compilato non lascia «chiave: ''» nel
 * documento, e quello che era già vuoto non conta come modifica.
 */
export function cleanAgainst(next: unknown, previous: unknown): unknown {
  if (Array.isArray(next)) {
    const before = Array.isArray(previous) ? previous : []
    return next.filter((item) => item !== null && item !== undefined).map((item, index) => cleanAgainst(item, before[index]))
  }
  if (isBag(next)) {
    const before = isBag(previous) ? previous : {}
    const out: Bag = {}
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === undefined) continue
      if (value === '' && (before[key] === undefined || before[key] === null)) continue
      out[key] = cleanAgainst(value, before[key])
    }
    return out
  }
  return next
}

/** Quello che si scrive quando una persona svuota un campo che aveva un valore: mai `null`. */
export function emptyLike(previous: unknown): unknown {
  if (Array.isArray(previous)) return []
  if (isBag(previous)) return {}
  if (typeof previous === 'boolean') return false
  return ''
}

/** Il valore da mandare all'API per un campo di primo livello, oppure `undefined` se non c'è niente da scrivere. */
export function payloadFor(next: unknown, previous: unknown): unknown {
  const cleaned = cleanAgainst(next, previous)
  if (cleaned === undefined || cleaned === null) return previous === undefined || previous === null ? undefined : emptyLike(previous)
  return cleaned
}

/** Il campo è cambiato davvero, ignorando null/vuoti che non erano già lì. */
export function isChanged(next: unknown, previous: unknown): boolean {
  const a = cleanAgainst(next, previous)
  const b = cleanAgainst(previous, previous)
  return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)
}

// ----- Percorsi con il punto ----------------------------------------------------------

/** Legge «extra.assets.0.url»: i numeri entrano negli elenchi. */
export function getAtPath(source: unknown, dotted: string): unknown {
  let current: unknown = source
  for (const part of dotted.split('.')) {
    if (Array.isArray(current)) current = /^\d+$/.test(part) ? current[Number(part)] : undefined
    else if (isBag(current)) current = current[part]
    else return undefined
  }
  return current
}

/** Copia di `source` con il valore messo al percorso (crea gli oggetti che mancano, rispetta gli elenchi). */
export function setAtPath<T extends Bag | unknown[]>(source: T, dotted: string, value: unknown): T {
  const [head, ...rest] = dotted.split('.')
  if (head === undefined || head === '') return source
  const child = (current: unknown): unknown => rest.length === 0 ? value : setAtPath((Array.isArray(current) || isBag(current) ? current : (/^\d+$/.test(rest[0] ?? '') ? [] : {})) as Bag, rest.join('.'), value)
  if (Array.isArray(source)) {
    const copy = [...source]
    copy[Number(head)] = child(source[Number(head)])
    return copy as T
  }
  return { ...source, [head]: child((source as Bag)[head]) } as T
}

const ADDRESS_KEYS = ['url', 'src', 'path'] as const

/**
 * Dove sta davvero l'immagine principale, come percorso che si può scrivere.
 * `source` è quello che dice `mainImageOfContent`: un campo (`cover`, `hero.image`),
 * oppure «extra.assets» e «gallery» (si cerca la voce), oppure «x.*» (trovata a
 * scansione: non si sa dove sia, quindi non si modifica da qui).
 */
export function editableImagePath(data: Bag, source: string | null | undefined): string | null {
  if (!source || source.startsWith('section:') || source.endsWith('.*')) return null
  if (source === 'extra.assets' || source === 'gallery') {
    const list = getAtPath(data, source)
    if (!Array.isArray(list)) return null
    for (let index = 0; index < list.length; index += 1) {
      const item = list[index]
      if (typeof item === 'string' && item.trim() && looksLikeImageAddress(item)) return `${source}.${index}`
      if (isBag(item) && (item.type === undefined || item.type === 'image')) {
        const key = ADDRESS_KEYS.find((name) => typeof item[name] === 'string' && (item[name] as string).trim())
        if (key) return `${source}.${index}.${key}`
      }
    }
    return null
  }
  return source
}

/** Un oggetto vuoto «nella forma» di un altro: stessa struttura, valori azzerati. Serve per «Aggiungi» in un elenco. */
export function blankLike(sample: unknown): unknown {
  if (Array.isArray(sample)) return []
  if (isBag(sample)) return Object.fromEntries(Object.entries(sample).map(([key, value]) => [key, blankLike(value)]))
  if (typeof sample === 'boolean') return false
  if (typeof sample === 'number') return ''
  return ''
}

/** Un nome corto per una riga di elenco: il primo testo che c'è, altrimenti il nome del file o il tipo. */
export function summaryOf(item: unknown): string {
  const clean = (text: string) => text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)
  if (typeof item === 'string') return clean(item)
  if (!isBag(item)) return ''
  const preferred = ['title', 'titolo', 'etichetta', 'label', 'name', 'nome', 'question', 'text']
  const isAddress = (value: string) => /^(https?:|\/)/.test(value.trim())
  for (const key of [...preferred, ...Object.keys(item)]) {
    const value = item[key]
    if (key === 'type' || typeof value !== 'string' || !value.trim() || isTechnicalKey(key) || isAddress(value)) continue
    return clean(value)
  }
  for (const key of ['url', 'src', 'path', 'href']) {
    const value = item[key]
    if (typeof value === 'string' && value.trim()) return clean(decodeURIComponent(value.trim().split(/[?#]/)[0]?.split('/').filter(Boolean).pop() ?? value))
  }
  return typeof item.type === 'string' ? clean(item.type) : ''
}

/** Quante voci dell'oggetto hanno un valore: per l'intestazione di un gruppo chiuso. */
export function filledCount(value: unknown): number {
  if (Array.isArray(value)) return value.length
  if (isBag(value)) return Object.entries(value).filter(([key, child]) => !isTechnicalKey(key) && !isBlank(child) && !(Array.isArray(child) && child.length === 0)).length
  return isBlank(value) ? 0 : 1
}
